import { CATEGORY, classifyProduct } from "./menuCategories";

// Poucas sugestões convertem mais que muitas: a chance de o cliente olhar
// alguma cai quando a lista cresce.
export const MAX_SUGGESTIONS = 6;

const { DRINK, ALCOHOL, DESSERT, SIDE, MAIN, COMBO } = CATEGORY;

/**
 * O que falta no pedido, em ordem de prioridade. Só complementos: nunca outro
 * prato, que seria uma alternativa ao que o cliente já escolheu.
 */
export function upsellGaps(cartCategories) {
  const has = (...categories) => categories.some((c) => cartCategories.has(c));
  const hasFood = has(MAIN, SIDE, COMBO);
  const gaps = [];

  if (!has(DRINK, ALCOHOL)) {
    // sem álcool primeiro: serve a qualquer pedido
    gaps.push({ categories: [DRINK, ALCOHOL], title: "Que tal uma bebida?" });
  }
  if (hasFood && !has(DESSERT)) {
    gaps.push({ categories: [DESSERT], title: "Para a sobremesa" });
  }
  if (has(MAIN) && !has(SIDE)) {
    gaps.push({ categories: [SIDE], title: "Para acompanhar" });
  }
  if (!hasFood && has(DRINK, ALCOHOL)) {
    // só bebida na sacola (adega, bar): petiscos
    gaps.push({ categories: [SIDE], title: "Para acompanhar" });
  }

  return gaps;
}

/**
 * Sugestões do "Peça também" da sacola.
 *
 * - `candidates`: produtos que podem ser oferecidos agora (disponíveis e fora
 *   da sacola); a elegibilidade fica com quem chama.
 * - `cartProducts`: os produtos que já estão na sacola.
 * - `canQuickAdd`: se o "+" adiciona direto (sem abrir o produto).
 * - `belowMinimum`: a sacola ainda não chegou ao pedido mínimo da entrega;
 *   sem lacuna no pedido, sugere os complementos mais baratos para chegar lá.
 *
 * Em cada lacuna: destaques da loja primeiro, depois os com foto (o card sem
 * foto é um bloco cinza, que chama menos atenção), depois o que o "+"
 * adiciona direto, depois o mais barato.
 */
export function buildUpsell({
  candidates,
  cartProducts,
  groups,
  canQuickAdd = () => false,
  belowMinimum = false,
}) {
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const categoryOf = (product) =>
    classifyProduct(product, groupById.get(product.groupId));

  const cartCategories = new Set(cartProducts.map(categoryOf));
  const byCategory = new Map();
  candidates.forEach((product) => {
    const category = categoryOf(product);
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push(product);
  });

  const rank = (a, b) =>
    Number(!!b.isHighlight) - Number(!!a.isHighlight) ||
    Number(!!b.pictureUrl) - Number(!!a.pictureUrl) ||
    Number(canQuickAdd(b)) - Number(canQuickAdd(a)) ||
    price(a) - price(b);

  const picked = [];
  const titles = [];
  const gaps = upsellGaps(cartCategories);
  // as vagas se dividem entre as lacunas: bebida não toma a vez da sobremesa
  const perGap = Math.ceil(MAX_SUGGESTIONS / Math.max(gaps.length, 1));
  gaps.forEach((gap) => {
    const items = gap.categories
      .flatMap((category) => [...(byCategory.get(category) || [])].sort(rank))
      .filter((p) => !picked.includes(p))
      .slice(0, perGap);
    if (items.length) {
      picked.push(...items);
      titles.push(gap.title);
    }
  });

  if (!picked.length && belowMinimum) {
    const cheapest = [DRINK, DESSERT, SIDE, ALCOHOL]
      .flatMap((category) => byCategory.get(category) || [])
      .sort((a, b) => price(a) - price(b));
    return {
      title: "Peça também",
      items: cheapest.slice(0, MAX_SUGGESTIONS),
    };
  }

  return {
    // um título que diga por que a lista está ali; com mais de uma lacuna, um
    // geral
    title: titles.length === 1 ? titles[0] : "Para completar o pedido",
    items: picked.slice(0, MAX_SUGGESTIONS),
  };
}

function price(product) {
  const value = parseFloat(product.minPrice);
  return Number.isFinite(value) ? value : Infinity;
}
